import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import LockIcon from '@mui/icons-material/Lock';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { api } from '@/api/client';
import { formatDateTime } from '@/utils/format';
import type { Encounter } from '@/types/medical';

type EncounterListItem = Encounter & {
  appointment_id: string;
  patient_initials: string;
};

export function ClinicianEncountersPage() {
  const q = useApiQuery({
    queryKey: ['clinician', 'encounters'],
    queryFn:  () => api.get<EncounterListItem[]>('/api/medical/clinician/encounters/'),
  });

  return (
    <>
      <PageHeader
        title="Encounters"
        subtitle="All SOAP notes you've authored. Click to open the encounter or its addenda."
      />

      <DataState query={q} empty={{
        title: 'No encounters yet',
        description: 'Encounters are created when you start a consult from an appointment.',
      }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((e) => (
              <Card
                key={e.id}
                component={RouterLink}
                to={`/clinician/appointments/${e.appointment_id}`}
                sx={{ textDecoration: 'none', color: 'inherit',
                      '&:hover': { borderColor: 'primary.main' } }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 1.5,
                      bgcolor: 'background.subtle', color: 'primary.main',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <HistoryEduIcon />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Patient {e.patient_initials}
                        </Typography>
                        {e.locked && (
                          <Chip size="small"
                                icon={<LockIcon fontSize="small" />}
                                label="locked" variant="outlined" />
                        )}
                        {(e.addenda?.length ?? 0) > 0 && (
                          <Chip size="small"
                                label={`${e.addenda.length} addendum`}
                                variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary"
                                  className="tabular">
                        {formatDateTime(e.updated_at)}
                      </Typography>
                    </Box>
                    <ChevronRightIcon color="action" />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DataState>
    </>
  );
}
